import type { ReactNode } from 'react';

import { Dugme } from './temel';

/**
 * TABLO VE SAYFALAMA.
 *
 * `PersonelListesi` ve `PuantajDenetimi` tablolarını elle yazıyor; izin,
 * görev ve dönem ekranları da sayfalı liste gösterecek. İlkinde yazılıp
 * diğerlerinde kopyalanırsa beş ayrı "yükleniyor / boş / hata" davranışı
 * doğar ve biri düzeltildiğinde ötekiler geride kalır.
 *
 * SAYFALAMA TABLONUN İÇİNDE DEĞİL. Rehber ve ayarlar ekranları sayfasız
 * liste gösteriyor; birleşik olsaydı o iki ekran kullanmadığı bir sözleşmeyi
 * taşırdı. Sayfalı ekran ikisini alt alta koyar.
 */

export interface Sutun<T> {
  baslik: string;
  /** Hücre içeriği. Biçimleme (tarih, süre) çağıran tarafta yapılır. */
  hucre: (satir: T) => ReactNode;
  hizala?: 'sol' | 'sag';
  /** Sütun içeriği satır sonuna kaymasın (tarih, saat, tutar). */
  daralt?: boolean;
}

export function Tablo<T>({
  sutunlar,
  satirlar,
  anahtar,
  bekliyor = false,
  bosMetin = 'Kayıt bulunamadı.',
  satirBicimi,
}: {
  sutunlar: Sutun<T>[];
  satirlar: T[];
  /** Satır kimliği. Dizin KULLANILMAZ: sıralama değişince React satırları karıştırır. */
  anahtar: (satir: T) => string;
  bekliyor?: boolean;
  bosMetin?: string;
  satirBicimi?: (satir: T) => string | undefined;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {sutunlar.map((s) => (
              <th
                key={s.baslik}
                className={`px-3 py-2 font-medium ${s.hizala === 'sag' ? 'text-right' : ''}`}
              >
                {s.baslik}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          {/*
            BEKLEME VE BOŞLUK AYRI DURUMLAR. Tek satırda birleştirilseydi
            ilk yüklemede "Kayıt bulunamadı" görünür ve kullanıcı süzgeci
            gereksizce değiştirirdi.
          */}
          {bekliyor && (
            <tr>
              <td colSpan={sutunlar.length} className="px-3 py-8 text-center text-slate-500">
                Yükleniyor…
              </td>
            </tr>
          )}

          {!bekliyor && satirlar.length === 0 && (
            <tr>
              <td colSpan={sutunlar.length} className="px-3 py-8 text-center text-slate-500">
                {bosMetin}
              </td>
            </tr>
          )}

          {!bekliyor &&
            satirlar.map((satir) => (
              <tr
                key={anahtar(satir)}
                className={`hover:bg-slate-50 ${satirBicimi?.(satir) ?? ''}`}
              >
                {sutunlar.map((s) => (
                  <td
                    key={s.baslik}
                    className={`px-3 py-2 text-slate-700 ${s.hizala === 'sag' ? 'text-right' : ''} ${
                      s.daralt ? 'whitespace-nowrap' : ''
                    }`}
                  >
                    {s.hucre(satir)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * SAYFALAMA ÇUBUĞU.
 *
 * `toplam` SÜZGECE UYAN TÜM KAYITLARDIR, bu sayfadakiler değil — sunucu
 * sözleşmesi böyle. Sayfa sayısı buradan hesaplanır; sayfadaki kayıt
 * sayısından hesaplansaydı son sayfa hiç görünmezdi.
 */
export function Sayfalama({
  sayfa,
  sayfaBoyutu,
  toplam,
  sayfayaGit,
  bekliyor = false,
}: {
  sayfa: number;
  sayfaBoyutu: number;
  toplam: number;
  sayfayaGit: (yeni: number) => void;
  bekliyor?: boolean;
}) {
  const sonSayfa = Math.max(1, Math.ceil(toplam / sayfaBoyutu));

  return (
    <div className="flex items-center justify-between gap-4 text-sm text-slate-600">
      <span>
        {toplam} kayıt · sayfa {Math.min(sayfa, sonSayfa)}/{sonSayfa}
      </span>

      <div className="flex gap-2">
        <Dugme
          tur="ikincil"
          tikla={() => sayfayaGit(sayfa - 1)}
          pasif={sayfa <= 1 || bekliyor}
          cocuk="Önceki"
        />
        <Dugme
          tur="ikincil"
          tikla={() => sayfayaGit(sayfa + 1)}
          pasif={sayfa >= sonSayfa || bekliyor}
          cocuk="Sonraki"
        />
      </div>
    </div>
  );
}
